// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import checkoutSharedStyles from "@conklin/App/SharedStyles/CheckoutStyles";
import { xIsAuthenticated } from "@conklin/App/Store/Selectors/GeneralSelectors";
import { getSettingsCollection } from "@conklin/App/Store/Selectors/SettingsSelectors";
import StyledBox, { StyledBoxStyles } from "@conklin/XDL/Components/Common/Box";
import { sharedDateTimeOptions } from "@conklin/XDL/constants";
import IconAddProject from "@conklin/XDL/Icons/IconAddProject";
import IconPrint from "@conklin/XDL/Icons/IconPrint";
import { iconButtonCommonStyles } from "@conklin/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import openPrintDialog from "@insite/client-framework/Common/Utilities/openPrintDialog";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { canAddAllToList, getCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import { getHomePageUrl, getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const cart = getCartState(state, state.pages.onePageCheckoutConfirmation.cartId)?.value;

    const {
        wishListSettings: { allowMultipleWishLists },
    } = getSettingsCollection(state);

    return {
        cart,
        billTo: getBillToState(state, cart?.billToId)?.value,
        homePageUrl: getHomePageUrl(state),
        // XNG-Changes
        allowMultipleWishLists,
        canAddAllToList: canAddAllToList(cart),
        isAuthenticated: xIsAuthenticated(state),
        createAccountUrl: getPageLinkByPageType(state, "CreateAccountPage")?.url ?? "/MyAccount/CreateAccount",
    };
};

// XNG-Changes
const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface OnePageCheckoutConfirmationHeaderStyles {
    container?: GridContainerProps;
    titleGridItem?: GridItemProps;
    titleText?: TypographyPresentationProps;
    emailSentText?: TypographyPresentationProps;
    divider?: InjectableCss;
    orderInfoGridItem?: GridItemProps;
    orderNumberText?: TypographyPresentationProps;
    orderPlacedText?: TypographyPresentationProps;
    deliveryMethodText?: TypographyPresentationProps;
    buttonsGridItem?: GridItemProps;
    printButton?: ButtonPresentationProps;
    // XNG-Changes
    createAccountBox?: StyledBoxStyles;
    createAccountTitle?: TypographyPresentationProps;
    createAccountMessage?: TypographyPresentationProps;
}

export const headerStyles: OnePageCheckoutConfirmationHeaderStyles = {
    titleGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    titleText: {
        variant: "h2",
    },
    divider: {
        css: css`
            padding: 15px;
            height: 100%;
            width: 100%;
            overflow: hidden;
            border-bottom: 1px solid ${getColor("common.border")};
        `,
    },
    orderInfoGridItem: {
        width: [8, 8, 10, 10, 10],
        css: css`
            flex-direction: column;
        `,
    },
    deliveryMethodText: {
        css: css`
            margin-top: 10px;
        `,
    },
    orderNumberText: { variant: "h5" },
    buttonsGridItem: {
        width: [4, 4, 2, 2, 2],
        css: css`
            justify-content: flex-end;
        `,
    },
    printButton: {
        buttonType: "outline",
        variant: "secondary",
    },
};

const styles = headerStyles;

const Divider = styled.div`
    ${styles.divider?.css}
`;

const OnePageCheckoutConfirmationHeader = ({
    cart,
    billTo,
    // XNG-Changes
    fields,
    history,
    homePageUrl,
    createAccountUrl,
    canAddAllToList,
    isAuthenticated,
    allowMultipleWishLists,
    setAddToListModalIsOpen,
    addToWishList,
}: Props) => {
    const printOrOpenPrintAllModal = () => {
        openPrintDialog();
    };

    // XNG-Changes
    const toasterContext = useContext(ToasterContext);

    if (!cart || !billTo) {
        return null;
    }

    // XNG-Changes
    const addAllToListClickHandler = () => {
        const productInfos =
            cart.cartLines
                ?.filter(o => o.productId && o.qtyOrdered)
                .map(cartLine => ({
                    productId: cartLine.productId!,
                    qtyOrdered: cartLine.qtyOrdered!,
                    unitOfMeasure: cartLine.unitOfMeasure,
                })) || [];
        if (!allowMultipleWishLists) {
            addToWishList({
                productInfos,
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
                onComplete(resultProps) {
                    if (resultProps.result?.wishList) {
                        // "this" is targeting the object being created, not the parent SFC
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onSuccess?.(resultProps.result.wishList);
                    } else if (resultProps.result?.errorMessage) {
                        toasterContext.addToast({ body: resultProps.result.errorMessage, messageType: "danger" });
                    }
                },
            });
            return;
        }

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos });
    };

    // XNG-Changes: structure
    return (
        <StyledWrapper {...styles.container}>
            <Typography as="h2" {...styles.titleText} data-test-selector="orderConfirmation_Success">
                {siteMessage("OrderConfirmation_Success")}
            </Typography>
            <StyledWrapper {...styles.orderInfoGridItem}>
                <StyledWrapper>
                    <Typography
                        {...checkoutSharedStyles.sectionTitle}
                        data-test-selector="orderConfirmation_orderPlaced"
                    >
                        {translate("Order Date")}
                    </Typography>
                    <Typography {...checkoutSharedStyles.sectionValue}>
                        <LocalizedDateTime
                            dateTime={cart.orderDate}
                            // XNG-Changes
                            options={sharedDateTimeOptions}
                        />
                    </Typography>
                </StyledWrapper>
                <StyledWrapper>
                    <Typography
                        {...checkoutSharedStyles.sectionTitle}
                        data-test-selector="orderConfirmation_orderStatus"
                    >
                        {translate("Order Status")}
                    </Typography>
                    <Typography {...checkoutSharedStyles.sectionValue}>{cart.statusDisplay}</Typography>
                </StyledWrapper>
                {/* XNG-Changes: hide when erp number does not exist */}
                {cart.erpOrderNumber && (
                    <StyledWrapper>
                        <Typography
                            {...checkoutSharedStyles.sectionTitle}
                            data-test-selector="orderConfirmation_orderNumber"
                        >
                            {translate("Order Number")}
                        </Typography>
                        <Typography {...checkoutSharedStyles.sectionValue}>
                            {cart.erpOrderNumber || cart.orderNumber}
                        </Typography>
                    </StyledWrapper>
                )}
                {/* XNG-Changes: Shipping data moved to OnePageCheckoutConfirmationShipping widget 
                <Typography as="p" {...styles.deliveryMethodText} data-test-selector="orderConfirmation_deliveryMethod">
                    {translate("Delivery method")}:&nbsp;
                    {cart.fulfillmentMethod === FulfillmentMethod.PickUp
                        ? translate("Store pickup at ") +
                          (cart.defaultWarehouse?.description || cart.defaultWarehouse?.name || "")
                        : cart.carrier && cart.shipVia
                        ? `${translate("Ship to home via")} ${cart.carrier.description} - ${cart.shipVia.description}`
                        : translate("Ship to home")}
                </Typography> 
                */}
            </StyledWrapper>
            <Typography as="p" {...styles.emailSentText} data-test-selector="orderConfirmation_EmailSent">
                {siteMessage("OrderConfirmation_EmailSent", billTo.email)}
            </Typography>
            <StyledWrapper {...styles.buttonsGridItem}>
                {/* XNG-Changes */}
                <Button
                    variant="primary"
                    sizeVariant="medium"
                    onClick={() => {
                        history.push(homePageUrl);
                    }}
                    data-test-selector="orderConfirmation_continueShoppingButton"
                >
                    {translate("Continue Shopping")}
                </Button>
                <Button
                    {...iconButtonCommonStyles(IconPrint, 12)}
                    onClick={printOrOpenPrintAllModal}
                    data-test-selector="orderConfirmation_printButton"
                >
                    {translate("Print Order")}
                </Button>
                {/* XNG-Changes */}
                {isAuthenticated && canAddAllToList && (
                    <Button
                        {...iconButtonCommonStyles(IconAddProject, 12)}
                        onClick={addAllToListClickHandler}
                        key="addAll"
                        data-test-selector="cartActionsAddAllToList"
                    >
                        {translate("Add Items to Project")}
                    </Button>
                )}
            </StyledWrapper>
            {!isAuthenticated && (
                <StyledBox extendedStyles={styles.createAccountBox}>
                    <StyledWrapper>
                        <Typography {...styles.createAccountTitle}>{fields.createAccountTitle}</Typography>
                        <Typography {...styles.createAccountMessage}>{fields.createAccountMessage}</Typography>
                    </StyledWrapper>
                    <Button
                        variant="primary"
                        sizeVariant="medium"
                        onClick={() => {
                            history.push(createAccountUrl);
                        }}
                        data-test-selector="orderConfirmation_continueShoppingButton"
                    >
                        {translate("Create An Account")}
                    </Button>
                </StyledBox>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    // XNG-Changes: add mapDispatchToProps
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(OnePageCheckoutConfirmationHeader)),
    definition: {
        displayName: "Header",
        group: "One Page Checkout Confirmation",
        allowedContexts: ["OnePageCheckoutConfirmationPage"],
        fieldDefinitions: [
            {
                name: "createAccountTitle",
                displayName: "Create Account Title",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
            },
            {
                name: "createAccountMessage",
                displayName: "Create Account Message",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
            },
        ],
    },
};

export default widgetModule;
